import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { Alert, App, Empty, Select, Switch, Tooltip } from "antd";
import {
  InfoCircleOutlined,
  MessageOutlined,
  PhoneOutlined,
  PlusOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import theme from "../../themes";
import { filterControlCss } from "../../components/ContentLoading";
import {
  pageShellCss,
  pageTitleCss,
  wrapRowCss,
} from "../../styles/responsive";
import CreateLineModal from "./CreateLineModal";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  assignAgentToLineThunk,
  getGoogleAuthInfoThunk,
  getLinesThunk,
  updateLineThunk,
  createGoogleAuthInfoThunk,
} from "../../store/actions/line.action";
import { getAgentsThunk } from "../../store/actions/agent.action";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import { ROLES } from "../../models/user.model";
import { IAgent, resolveAgentChannels } from "../../models/agent.model";
import {
  ILineResponse,
  IUpdateLine,
  isCallChannelOn,
  isSmsChannelOn,
  isWhatsAppEnabled,
  lineAgentId,
  smsAgentId,
  LineInboundType,
  LineStatus,
  PhoneService,
} from "../../models/line.model";
import { canSeeEmailChannel } from "../../models/company.model";
import { useChannelSettings } from "../../hooks/useChannelSettings";
import { useSearchParams } from "react-router-dom";
import SipSettings from "./SipSettings";

type SettingsTab = "phones" | "email" | "integrations" | "sip";

const TAB_VALUES: SettingsTab[] = ["phones", "email", "integrations", "sip"];
const UNASSIGN_AGENT = "__none__";
const CHANNEL_SUPPORT_EMAIL = "support@lytte.ai";

const isSettingsTab = (value: string | null): value is SettingsTab =>
  value !== null && (TAB_VALUES as string[]).includes(value);

const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message?: unknown }).message || "").trim();
    if (message) return message;
  }
  return fallback;
};

const AssignedAgentLabel: React.FC = () => {
  const { t } = useTranslation();
  const hint = t("settings.v2.assignedAgentHint");

  return (
    <LabelWithHint>
      {t("settings.v2.assignedAgent")}
      <Tooltip title={hint} trigger={["hover", "focus", "click"]} placement="top">
        <InfoHintButton type="button" aria-label={hint}>
          <InfoCircleOutlined aria-hidden />
        </InfoHintButton>
      </Tooltip>
    </LabelWithHint>
  );
};

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const { user } = useAppSelector((state) => state.auth);
  const { googleAuthInfo, lines } = useAppSelector((state) => state.line);
  const { flags, isEnabled } = useChannelSettings();
  const [tab, setTab] = useState<SettingsTab>(() => {
    const requested = searchParams.get("tab");
    if (requested === "channels") return "phones";
    return isSettingsTab(requested) ? requested : "phones";
  });
  const [isCreatePhoneOpen, setIsCreatePhoneOpen] = useState(false);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [busyLineChannel, setBusyLineChannel] = useState<string | null>(null);
  const lineChannelBusyKey = (
    lineId: string,
    channel: "call" | "sms" | "whatsapp"
  ) => `${lineId}:${channel}`;
  const isLineChannelBusy = (
    lineId: string,
    channel: "call" | "sms" | "whatsapp"
  ) => busyLineChannel === lineChannelBusyKey(lineId, channel);
  const canSeeMail = canSeeEmailChannel(user);
  const showEmail = canSeeMail && flags.email;
  const canToggleMessaging = Boolean(user?.is_super_admin);
  const isReadOnly = user?.role === ROLES.VIEW_ONLY && !user?.is_super_admin;

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const redirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || "";
  const scope = encodeURIComponent(
    "https://www.googleapis.com/auth/calendar " +
      "https://www.googleapis.com/auth/calendar.events " +
      "https://www.googleapis.com/auth/userinfo.profile " +
      "https://www.googleapis.com/auth/userinfo.email"
  );
  const oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(
    redirectUri
  )}&response_type=code&scope=${scope}&access_type=offline&include_granted_scopes=true&prompt=consent`;

  const fetchLines = async () => {
    await dispatch(
      getLinesThunk({ limit: 999, page: 1, line_number: "" })
    ).unwrap();
  };

  const fetchAgents = async () => {
    const res = await dispatch(
      getAgentsThunk({
        limit: 999,
        page: 1,
        keyword: "",
        direction: "inbound",
      })
    ).unwrap();
    setAgents(res.data.agents || []);
  };

  useEffect(() => {
    dispatch(getGoogleAuthInfoThunk());
    fetchLines().catch(() => undefined);
    fetchAgents().catch(() => undefined);
    const params = new URLSearchParams(window.location.search);
    const authCode = params.get("code");
    if (authCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
      dispatch(createGoogleAuthInfoThunk(authCode))
        .unwrap()
        .then(() => {
          toast.success(
            t("settings.googleCalendarConnectedSuccessfully"),
            5,
            <CheckCircleIcon />
          );
          dispatch(getGoogleAuthInfoThunk());
        })
        .catch(() => toast.error(t("settings.failedToConnectGoogleCalendar"), 5));
    }
  }, []);

  useEffect(() => {
    if (tab === "email" && !showEmail) setTab("phones");
  }, [tab, showEmail]);

  const handleToggleLineChannel = async (
    line: ILineResponse,
    channel: "call" | "sms" | "whatsapp",
    checked: boolean
  ) => {
    if (isReadOnly) {
      return;
    }
    if ((channel === "sms" || channel === "whatsapp") && !canToggleMessaging) {
      return;
    }
    const flag: keyof IUpdateLine =
      channel === "call"
        ? "call_enabled"
        : channel === "sms"
          ? "sms_enabled"
          : "whatsapp_enabled";
    if (line.active_campaigns?.length) {
      toast.warning(
        t("settings.v2.campaignLock", {
          names: line.active_campaigns
            .map((campaign) => campaign.campaign_name)
            .filter(Boolean)
            .join(", "),
        }),
        6
      );
      return;
    }
    try {
      setBusyLineChannel(lineChannelBusyKey(line._id, channel));
      await dispatch(
        updateLineThunk({ id: line._id, payload: { [flag]: checked } })
      ).unwrap();
      await fetchLines();
      toast.success(
        t(
          checked
            ? `settings.v2.${channel}Enabled`
            : `settings.v2.${channel}Disabled`
        ),
        5,
        <CheckCircleIcon />
      );
    } catch (error) {
      toast.error(
        errorMessage(error, t(`settings.v2.${channel}ToggleFailed`)),
        5
      );
    } finally {
      setBusyLineChannel(null);
    }
  };

  const handleAssign = async (
    lineId: string,
    agentId: string,
    inboundType: LineInboundType
  ) => {
    if (isReadOnly) {
      return;
    }
    const assignChannel: "call" | "sms" | "whatsapp" =
      inboundType === LineInboundType.SMS
        ? "sms"
        : inboundType === LineInboundType.WHATSAPP
          ? "whatsapp"
          : "call";
    const locked = (lines?.lines || []).find((row) => row._id === lineId);
    if (locked?.active_campaigns?.length) {
      toast.warning(
        t("settings.v2.campaignLock", {
          names: locked.active_campaigns
            .map((campaign) => campaign.campaign_name)
            .filter(Boolean)
            .join(", "),
        }),
        6
      );
      return;
    }
    const channelOn = locked
      ? assignChannel === "sms"
        ? isSmsChannelOn(locked)
        : assignChannel === "whatsapp"
          ? isWhatsAppEnabled(locked)
          : isCallChannelOn(locked)
      : false;
    if (!channelOn) {
      toast.warning(t(`settings.v2.${assignChannel}TurnOnFirst`), 6);
      return;
    }
    const toastKey =
      inboundType === LineInboundType.SMS
        ? agentId
          ? "settings.assignAgents.smsAgentAssignedSuccessfully"
          : "settings.assignAgents.smsAgentUnassignedSuccessfully"
        : inboundType === LineInboundType.WHATSAPP
          ? agentId
            ? "settings.assignAgents.whatsappAgentAssignedSuccessfully"
            : "settings.assignAgents.whatsappAgentUnassignedSuccessfully"
          : agentId
            ? "settings.assignAgents.phoneAgentAssignedSuccessfully"
            : "settings.assignAgents.phoneAgentUnassignedSuccessfully";
    const failKey =
      inboundType === LineInboundType.SMS
        ? "settings.assignAgents.failedToAssignSmsAgent"
        : inboundType === LineInboundType.WHATSAPP
          ? "settings.assignAgents.failedToAssignWhatsappAgent"
          : "settings.assignAgents.failedToAssignPhoneAgent";
    try {
      setBusyLineChannel(lineChannelBusyKey(lineId, assignChannel));
      await dispatch(
        assignAgentToLineThunk({
          id: lineId,
          agentId: agentId || "",
          inbound_type: inboundType,
        })
      ).unwrap();
      await fetchLines();
      toast.success(t(toastKey), 5, <CheckCircleIcon />);
    } catch (error) {
      toast.error(errorMessage(error, t(failKey)), 5);
    } finally {
      setBusyLineChannel(null);
    }
  };

  const lineRows = lines?.lines || [];
  const optionsFor = (channel: "call" | "sms" | "whatsapp") => {
    const matching: { value: string; label: string }[] = [];
    const rest: { value: string; label: string }[] = [];
    agents.forEach((agent) => {
      const option = { value: agent._id, label: agent.agent_name };
      if (resolveAgentChannels(agent.channels).includes(channel)) {
        matching.push(option);
      } else {
        rest.push(option);
      }
    });
    return [...matching, ...rest];
  };
  const phoneAgentOptions = optionsFor("call");
  const smsAgentOptions = optionsFor("sms");
  const whatsappAgentOptions = optionsFor("whatsapp");
  const withUnassign = (options: { value: string; label: string }[]) => [
    {
      value: UNASSIGN_AGENT,
      label: t("settings.v2.unassignCallAgent"),
    },
    ...options,
  ];

  const statusLabel = (status: number) => {
    if (status === LineStatus.ACTIVE) return t("settings.managePhoneNumber.statuses.active");
    if (status === LineStatus.PAUSED) return t("settings.managePhoneNumber.statuses.paused");
    if (status === LineStatus.MESSAGE_SERVICE_PENDING) {
      return t("settings.managePhoneNumber.statuses.messageServicePending");
    }
    return t("settings.managePhoneNumber.statuses.unknown");
  };

  return (
    <Page>
      <Header>
        <Title>{t("settings.title")}</Title>
        {tab === "phones" && user?.role === ROLES.ADMIN && (
          <PrimaryButton type="button" onClick={() => setIsCreatePhoneOpen(true)}>
            <PlusOutlined /> {t("settings.v2.addPhoneNumber")}
          </PrimaryButton>
        )}
      </Header>

      <Tabs>
        <Tab type="button" $active={tab === "phones"} onClick={() => setTab("phones")}>
          {t("settings.v2.phoneNumbers")}
        </Tab>
        {showEmail && (
          <Tab type="button" $active={tab === "email"} onClick={() => setTab("email")}>
            {t("settings.v2.emailAddresses")}
          </Tab>
        )}
        <Tab
          type="button"
          $active={tab === "integrations"}
          onClick={() => setTab("integrations")}
        >
          {t("settings.v2.integrations")}
        </Tab>
        <Tab type="button" $active={tab === "sip"} onClick={() => setTab("sip")}>
          {t("settings.sip.tab")}
        </Tab>
      </Tabs>

      {tab === "phones" && (
        <Stack>
          <ChannelIntro>
            <ChannelSupportNote
              id="channel-support-note"
              type="warning"
              showIcon
              message={t("settings.v2.channelsSupportNote")}
              description={
                <Trans
                  i18nKey="settings.v2.channelsSupportContact"
                  components={{
                    mail: (
                      <ChannelSupportLink
                        href={`mailto:${CHANNEL_SUPPORT_EMAIL}`}
                      />
                    ),
                  }}
                />
              }
            />
          </ChannelIntro>
          <Hint>{t("settings.v2.phoneHint")}</Hint>
          {lineRows.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("settings.v2.noNumbers")}
            >
              {user?.role === ROLES.ADMIN && (
                <EmptyActions>
                  <PrimaryButton
                    type="button"
                    onClick={() => setIsCreatePhoneOpen(true)}
                  >
                    <PlusOutlined /> {t("settings.v2.addPhoneNumber")}
                  </PrimaryButton>
                </EmptyActions>
              )}
            </Empty>
          ) : (
            lineRows.map((line) => {
              const live = line.status === LineStatus.ACTIVE;
              const hasSms = line.phone_service === PhoneService.CALLS_SMS;
              const callAgent = lineAgentId(line.attached_inbound_agent);
              const smsAgent = smsAgentId(line);
              const whatsappAgent = lineAgentId(line.attached_whatsapp_agent);
              const callOn = isCallChannelOn(line);
              const smsOn = isSmsChannelOn(line);
              const whatsappOn = isWhatsAppEnabled(line);
              const activeCampaigns = line.active_campaigns || [];
              const routingLocked = activeCampaigns.length > 0;
              const lockReason = routingLocked
                ? t("settings.v2.campaignLock", {
                    names: activeCampaigns
                      .map((campaign) => campaign.campaign_name)
                      .filter(Boolean)
                      .join(", "),
                  })
                : "";
              const assignChange =
                (inboundType: LineInboundType) => (value: string | undefined) => {
                  handleAssign(
                    line._id,
                    !value || value === UNASSIGN_AGENT ? "" : value,
                    inboundType
                  );
                };
              return (
                <NumberCard key={line._id}>
                  <NumberHead>
                    <PhoneOutlined style={{ color: theme.colors.green1, fontSize: 20 }} />
                    <NumberCopy>
                      <strong>{line.line_number || line.company_number}</strong>
                      <Muted>
                        {line.name}
                        {line.phone_service === PhoneService.CALLS_SMS
                          ? ` · ${t("settings.managePhoneNumber.services.callsAndMessages")}`
                          : ` · ${t("settings.managePhoneNumber.services.callsOnly")}`}
                      </Muted>
                    </NumberCopy>
                    <StatusChip $live={live}>{statusLabel(line.status)}</StatusChip>
                  </NumberHead>
                  {routingLocked && (
                    <LockBanner>{lockReason}</LockBanner>
                  )}
                  <ChannelGrid>
                    <ChannelHeader>
                      <GridHead>{t("settings.v2.channel")}</GridHead>
                      <GridHead>{t("settings.v2.enabled")}</GridHead>
                      <GridHead>
                        <AssignedAgentLabel />
                      </GridHead>
                    </ChannelHeader>

                    {isEnabled("call") && (
                      <ChannelRow>
                        <ChannelLabel>
                          <PhoneOutlined /> {t("dashboard.v2.phoneCall")}
                        </ChannelLabel>
                        <ChannelField>
                          <ChannelFieldLabel>
                            {t("settings.v2.enabled")}
                          </ChannelFieldLabel>
                          <Switch
                            checked={callOn}
                            loading={isLineChannelBusy(line._id, "call")}
                            disabled={routingLocked || isReadOnly}
                            onChange={(checked) =>
                              handleToggleLineChannel(line, "call", checked)
                            }
                          />
                        </ChannelField>
                        <ChannelField>
                          <ChannelFieldLabel>
                            <AssignedAgentLabel />
                          </ChannelFieldLabel>
                          <Select
                            key={`${line._id}-call-agent`}
                            allowClear
                            placeholder={t(
                              "settings.assignAgents.selectPhoneAgent"
                            )}
                            value={callAgent || null}
                            options={withUnassign(phoneAgentOptions)}
                            disabled={
                              !callOn ||
                              routingLocked ||
                              isLineChannelBusy(line._id, "call") ||
                              isReadOnly
                            }
                            onChange={assignChange(LineInboundType.CALL)}
                            style={{ width: "100%" }}
                          />
                        </ChannelField>
                      </ChannelRow>
                    )}

                    <ChannelRow>
                      <ChannelLabel>
                        <MessageOutlined /> {t("dashboard.v2.textMessages")}
                      </ChannelLabel>
                      <ChannelField>
                        <ChannelFieldLabel>
                          {t("settings.v2.enabled")}
                        </ChannelFieldLabel>
                        <Switch
                          checked={isEnabled("sms") && smsOn}
                          disabled={
                            !isEnabled("sms") ||
                            !hasSms ||
                            routingLocked ||
                            !canToggleMessaging ||
                            isReadOnly
                          }
                          loading={isLineChannelBusy(line._id, "sms")}
                          aria-describedby="channel-support-note"
                          onChange={(checked) =>
                            handleToggleLineChannel(line, "sms", checked)
                          }
                        />
                      </ChannelField>
                      <ChannelField>
                        <ChannelFieldLabel>
                          <AssignedAgentLabel />
                        </ChannelFieldLabel>
                        <Select
                          key={`${line._id}-sms-agent`}
                          allowClear
                          placeholder={t(
                            "settings.assignAgents.selectInboundAgent"
                          )}
                          value={smsAgent || null}
                          options={withUnassign(smsAgentOptions)}
                          disabled={
                            !hasSms ||
                            !isEnabled("sms") ||
                            !smsOn ||
                            routingLocked ||
                            isLineChannelBusy(line._id, "sms") ||
                            isReadOnly
                          }
                          onChange={assignChange(LineInboundType.SMS)}
                          style={{ width: "100%" }}
                        />
                      </ChannelField>
                    </ChannelRow>

                    <ChannelRow>
                      <ChannelLabel>
                        <WhatsAppOutlined /> {t("dashboard.v2.whatsappMessages")}
                      </ChannelLabel>
                      <ChannelField>
                        <ChannelFieldLabel>
                          {t("settings.v2.enabled")}
                        </ChannelFieldLabel>
                        <Switch
                          checked={isEnabled("whatsapp") && whatsappOn}
                          loading={isLineChannelBusy(line._id, "whatsapp")}
                          disabled={
                            !isEnabled("whatsapp") ||
                            routingLocked ||
                            !canToggleMessaging ||
                            isReadOnly
                          }
                          aria-describedby="channel-support-note"
                          onChange={(checked) =>
                            handleToggleLineChannel(
                              line,
                              "whatsapp",
                              checked
                            )
                          }
                        />
                      </ChannelField>
                      <ChannelField>
                        <ChannelFieldLabel>
                          <AssignedAgentLabel />
                        </ChannelFieldLabel>
                        <Select
                          key={`${line._id}-whatsapp-agent`}
                          allowClear
                          placeholder={t(
                            "settings.assignAgents.selectWhatsappAgent"
                          )}
                          value={whatsappAgent || null}
                          options={withUnassign(whatsappAgentOptions)}
                          disabled={
                            !isEnabled("whatsapp") ||
                            !whatsappOn ||
                            routingLocked ||
                            isLineChannelBusy(line._id, "whatsapp") ||
                            isReadOnly
                          }
                          onChange={assignChange(LineInboundType.WHATSAPP)}
                          style={{ width: "100%" }}
                        />
                      </ChannelField>
                    </ChannelRow>
                  </ChannelGrid>
                </NumberCard>
              );
            })
          )}
        </Stack>
      )}

      {tab === "email" && showEmail && (
        <Stack>
          <Hint>{t("settings.v2.emailHint")}</Hint>
          <Panel>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("settings.v2.noEmail")}
            />
          </Panel>
        </Stack>
      )}

      {tab === "integrations" && (
        <Stack>
          <Hint>{t("settings.v2.integrationsHint")}</Hint>
          <Panel>
            <IntegrationRow>
              <div>
                <strong>{t("settings.calendar")} (beta)</strong>
                {googleAuthInfo?.data ? (
                  <Muted>
                    {t("settings.connected")}
                    {googleAuthInfo.data.email ? ` · ${googleAuthInfo.data.email}` : ""}
                  </Muted>
                ) : (
                  <Muted>{t("settings.v2.calendarOff")}</Muted>
                )}
              </div>
              {user?.role === ROLES.ADMIN && (
                <GhostLink href={oauthUrl}>
                  {googleAuthInfo?.data
                    ? t("settings.connectToAnotherAccount")
                    : t("settings.connectGoogleCalendar")}
                </GhostLink>
              )}
            </IntegrationRow>
          </Panel>
        </Stack>
      )}

      {tab === "sip" && <SipSettings />}

      <CreateLineModal
        open={isCreatePhoneOpen}
        onCancel={() => setIsCreatePhoneOpen(false)}
        onSuccess={() => {
          setIsCreatePhoneOpen(false);
          fetchLines();
        }}
      />
    </Page>
  );
};

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  gap: 26px;
  ${pageShellCss}
`;

const Header = styled.div`
  ${wrapRowCss}
  min-height: 48px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;
  ${pageTitleCss}
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: ${theme.colors.green1};
  color: #fff;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  ${filterControlCss}
`;

const EmptyActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  border-bottom: 1px solid ${theme.colors.grey2};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0 2px 14px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  border: none;
  background: none;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? theme.colors.green1 : theme.colors.grey1)};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  border-bottom: 2px solid ${({ $active }) => ($active ? theme.colors.green1 : "transparent")};
  ${filterControlCss}

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: ${theme.colors.grey1};
  max-width: 820px;
`;

const ChannelIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 820px;
`;

const ChannelSupportNote = styled(Alert)`
  margin: 0;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.45;

  .ant-alert-icon {
    margin-top: 1px;
    font-size: 16px;
  }

  .ant-alert-message,
  .ant-alert-description {
    font-size: 13px;
    line-height: 1.45;
  }

  .ant-alert-description {
    margin-top: 2px;
  }
`;

const ChannelSupportLink = styled.a`
  color: ${theme.colors.yellow4};
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover,
  &:focus-visible {
    color: #8a6414;
  }
`;

const NumberCard = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  overflow: hidden;
`;

const NumberHead = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.colors.grey2};
  flex-wrap: wrap;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const NumberCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  min-width: 0;
  strong {
    font-weight: 600;
    font-size: 16px;
    line-height: 1;
    color: #000;
  }
`;

const Muted = styled.span`
  font-size: 14px;
  line-height: 1.3;
  color: ${theme.colors.grey1};
`;

const StatusChip = styled.span<{ $live: boolean }>`
  padding: 5px 10px;
  border-radius: 6px;
  background: ${({ $live }) => ($live ? theme.colors.green2 : theme.colors.grey5)};
  color: ${({ $live }) => ($live ? theme.colors.green1 : theme.colors.grey1)};
  font-size: 14px;
  line-height: 1;
`;

const LockBanner = styled.p`
  margin: 0;
  padding: 12px 24px;
  border-bottom: 1px solid rgba(253, 196, 72, 0.42);
  background: ${theme.colors.yellow3};
  color: #5c4a1f;
  font-size: 14px;
  line-height: 1.4;
`;

const channelColumnsCss = `
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: center;
`;

const ChannelGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 16px 16px;

  @media (max-width: 900px) {
    gap: 8px;
    padding: 8px 12px 16px;
  }
`;

const ChannelHeader = styled.div`
  ${channelColumnsCss}
  padding: 10px 16px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const ChannelRow = styled.div`
  ${channelColumnsCss}
  min-height: 64px;
  padding: 16px;
  background-color: ${theme.colors.grey3};
  border-radius: 16px;

  /* Header is child 1; first data row is child 2 (even) → grey3 like reports */
  &:nth-child(odd) {
    background-color: ${theme.colors.grey4};
  }

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 12px;
  }
`;

const GridHead = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${theme.colors.grey1};
`;

const LabelWithHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
`;

const InfoHintButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #bcbcbc;
  font-size: 14px;
  line-height: 1;
  cursor: help;

  &:hover,
  &:focus-visible {
    color: ${theme.colors.grey1};
    outline: none;
  }
`;

const ChannelField = styled.div`
  min-width: 0;
  width: 100%;
`;

const ChannelFieldLabel = styled.span`
  display: none;
  font-size: 13px;
  line-height: 1;
  color: ${theme.colors.grey1};
  margin-bottom: 6px;

  @media (max-width: 900px) {
    display: block;
  }
`;

const ChannelLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  line-height: 1;
  color: #000;
  .anticon {
    font-size: 20px;
    color: rgb(31, 31, 31);
  }

  @media (max-width: 900px) {
    font-weight: 600;
  }
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.09);
  padding: 24px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const IntegrationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  strong {
    display: block;
    font-size: 16px;
    margin-bottom: 7px;
  }
`;

const GhostLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 11px 16px;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  background: #fff;
  color: #000;
  font-size: 16px;
  text-decoration: none;
`;

export default Settings;
